import React, { FC, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { maskPersonalIdentityNumbers } from 'utils/mask-personal-identity-numbers';

type AssistantProblemReportProps = {
    draft: string;
    /** Sends the report as a Zendesk ticket; resolves false when it failed. */
    onSend: (report: string) => Promise<boolean>;
};

/**
 * DD-3780: a problem report the assistant drafted. The user edits it and decides
 * whether to send it; nothing goes to support without the click.
 */
const AssistantProblemReport: FC<AssistantProblemReportProps> = ({ draft, onSend }: AssistantProblemReportProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const [report, setReport] = useState(draft);
    const [state, setState] = useState<'DRAFT' | 'SENDING' | 'SENT' | 'FAILED' | 'DISCARDED'>('DRAFT');

    if (state === 'SENT') {
        return <Alert severity="success">{translate('help-drawer:ASSISTANT_REPORT_SENT')}</Alert>;
    }
    if (state === 'DISCARDED') {
        return (
            <Typography variant="body2" color="grey">
                {translate('help-drawer:ASSISTANT_REPORT_DISCARDED')}
            </Typography>
        );
    }

    const send = async () => {
        setState('SENDING');
        setState((await onSend(maskPersonalIdentityNumbers(report.trim()))) ? 'SENT' : 'FAILED');
    };

    return (
        <Box mt={1}>
            <Typography variant="caption" color="grey">
                {translate('help-drawer:ASSISTANT_REPORT_INTRO')}
            </Typography>
            <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                value={report}
                onChange={(event) => setReport(event.target.value)}
                inputProps={{ maxLength: 5000 }}
                sx={{ my: 1, bgcolor: 'background.paper' }}
            />
            {state === 'FAILED' ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {translate('help-drawer:ASSISTANT_REPORT_FAILED')}
                </Alert>
            ) : undefined}
            <Box display="flex" gap={1}>
                <LoadingButton
                    variant="contained"
                    loading={state === 'SENDING'}
                    disabled={!report.trim()}
                    onClick={send}
                >
                    {translate('help-drawer:ASSISTANT_REPORT_SEND')}
                </LoadingButton>
                <Button variant="text" disabled={state === 'SENDING'} onClick={() => setState('DISCARDED')}>
                    {translate('help-drawer:ASSISTANT_REPORT_DISCARD')}
                </Button>
            </Box>
        </Box>
    );
};

export default AssistantProblemReport;
